import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { delay, of } from 'rxjs';
import { Bildungsangebot } from '../../model';
import { AngebotListItemComponent } from '../angebot-list-item/angebot-list-item.component';

@Component({
  selector: 'now-wbsuche-lib-angebot-list',
  templateUrl: './angebot-list.component.html',
  styleUrls: ['angebot-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, AngebotListItemComponent]
})
export class AngebotListComponent implements OnChanges {
  @Input()
  public angebote: Bildungsangebot[] = [];

  @Input()
  public selectedAngebotId?: number;

  @Output()
  public angebotSelected = new EventEmitter<number>();

  @ViewChildren('listItem', { read: ElementRef })
  private readonly listItems!: QueryList<ElementRef<HTMLLIElement>>;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.angebote || changes.selectedAngebotId) && this.selectedAngebotId) {
      this.focusedIndex = this.selectedAngebotIndex;
    }
  }

  public focusedIndex = -1;
  private lastClickTime = new Date();

  @HostListener('focusout')
  focusout(): void {
    this.focusedIndex = this.selectedAngebotIndex;
  }

  setFocusedAngebot(): void {
    const currentAngebot = this.angebote.find((_, index) => index === this.focusedIndex);
    if (currentAngebot) {
      this.selectAngebot(currentAngebot.id);
    }
  }

  focusin(): void {
    if (this.focusedIndex === -1) {
      this.focusedIndex = 0;
    }
    // Wenn die Zeit zwischen dem focusin und dem click auf das UL-Element
    // kurz ist, dann wurde vermutlich mittels click fokussiert
    // andern Falls wurde vermutlich mitteln Tab-Taste fokussiert
    // und dann soll das ausgewählte Listitem fokussiert weden.
    const lastFocusTime = new Date();
    of(null)
      .pipe(delay(150))
      .subscribe(() => {
        if (this.lastClickTime <= lastFocusTime) {
          // Das element soll nur in sicht gescrollt werden, wenn es vorher komplett aus dem viewport verschwunden ist,
          // sonst kam es manchmal zu komischen Rucklern
          if (
            this.focusedListItem &&
            !this.isElementInViewport(this.focusedListItem.nativeElement)
          ) {
            this.focusedListItem?.nativeElement.scrollIntoView();
          }
        }
      });
  }

  private isElementInViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) /* or $(window).height() */ &&
      rect.right <=
        (window.innerWidth || document.documentElement.clientWidth) /* or $(window).width() */
    );
  }

  onUlClick() {
    this.lastClickTime = new Date();
  }

  public selectAngebot(angebotId: number, event?: Event) {
    event?.preventDefault();
    this.angebotSelected.emit(angebotId);
  }

  public moveUp(): void {
    if (this.focusedIndex > 0) {
      this.focusedIndex = this.focusedIndex - 1;
      this.focusedListItem?.nativeElement.scrollIntoView();
    }
  }

  public moveDown(): void {
    if (this.focusedIndex + 1 < this.angebote.length) {
      this.focusedIndex = this.focusedIndex + 1;
      this.focusedListItem?.nativeElement.scrollIntoView();
    }
  }

  public getIsActive(index: number): boolean {
    if (this.focusedIndex != -1) {
      return this.focusedIndex === index;
    } else {
      return this.selectedAngebotIndex === index;
    }
  }

  public getListItemId(angebotId: number): string {
    return `list_item_${angebotId}`;
  }

  get selectedAngebotIndex(): number {
    const selectedAngebot = this.angebote.find((angebot) => angebot.id === this.selectedAngebotId);
    if (!!selectedAngebot) {
      return this.angebote.indexOf(selectedAngebot);
    }
    return -1;
  }

  get focusedListItem(): ElementRef<HTMLLIElement> {
    return this.listItems?.find((_, index) => {
      return index === this.focusedIndex;
    })!;
  }

  get activedescendant(): string {
    let angebot;
    if (this.focusedIndex != -1) {
      angebot = this.angebote.find((_, index) => index === this.focusedIndex);
    } else {
      angebot = this.angebote.find((angebot) => angebot.id === this.selectedAngebotId);
    }

    return !!angebot ? this.getListItemId(angebot.id) : '';
  }
}
